import { useRef, useState, useCallback, useMemo, useEffect, type ReactNode, type CSSProperties } from 'react';
import {
  UserIcon, UsersIcon, FaceSmileIcon, ExclamationTriangleIcon, ShieldExclamationIcon,
  TruckIcon, ShoppingBagIcon, ArrowTrendingDownIcon, BoltIcon, HandRaisedIcon, EyeIcon,
  MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon, ArrowsPointingOutIcon,
} from '@heroicons/react/24/solid';

/**
 * Recording scrubber/timeline — shared by both dashboards' recordings player. Fully
 * presentational: takes segments + the visible time range and reports seeks via onSeek.
 * (Cloud previously inlined an identical copy; both nodes use this now.)
 */
interface Segment {
  id?: string | number;
  start_time: string | number | Date;
  end_time: string | number | Date;
  /** Why this footage was kept: 'continuous' | 'event' | 'motion' | 'manual'. */
  trigger?: string;
  [key: string]: unknown;
}

/** A retention window from `recording_triggers` — one per window, not per trigger. */
interface TriggerMarker {
  id?: string | number;
  event_time: string | number | Date;
  source?: string;
  event_type?: string | null;
  trigger_count?: number;
}

/**
 * Retained-block colour by why it was kept. Continuous footage keeps the original green so
 * nothing changes for existing deployments; triggered clips are visually distinct because on
 * an event-mode camera the GAPS are normal and the operator needs to see at a glance which
 * blocks exist on purpose.
 */
export const TRIGGER_COLOURS: Record<string, string> = {
  continuous: '#6ee7b7',
  event: '#fbbf24',
  motion: '#38bdf8',
  manual: '#c084fc',
};

/**
 * The visible-window band on the overview/drag track.
 *
 * Deliberately NOT the brand token: brand is a per-deployment CSS variable, so on some
 * themes it lands close to a trigger colour and the window stops being distinguishable from
 * the footage it sits on. Indigo is the one hue not already taken by a trigger class
 * (mint/amber/sky/purple), the red playhead, or the blue progress fill.
 */
const VIEW_WINDOW_COLOUR = '#6366f1';

/** Human label per trigger class, for the legend and tooltips. */
export const TRIGGER_LABELS: Record<string, string> = {
  continuous: 'Continuous',
  event: 'Event',
  motion: 'Motion',
  manual: 'Manual',
};

/**
 * Symbol per detection category, pinned above the block it caused.
 *
 * `24/solid` rather than outline: these render at 11px, where outline strokes turn to mush.
 * The point is instant recognition — "a person was detected here" — not decoration.
 */
const EVENT_ICONS: Record<string, typeof UserIcon> = {
  person: UserIcon,
  person_count: UsersIcon,
  face: FaceSmileIcon,
  intrusion: ExclamationTriangleIcon,
  ppe: ShieldExclamationIcon,
  vehicle: TruckIcon,
  theft: ShoppingBagIcon,
  fall: ArrowTrendingDownIcon,
  other: EyeIcon,
};

const SOURCE_ICONS: Record<string, typeof UserIcon> = {
  motion: BoltIcon,
  manual: HandRaisedIcon,
};

const EVENT_LABELS: Record<string, string> = {
  person: 'Person detected',
  person_count: 'People counted',
  face: 'Face recognised',
  intrusion: 'Intrusion',
  ppe: 'PPE violation',
  vehicle: 'Vehicle',
  theft: 'Theft',
  fall: 'Fall detected',
  other: 'Detection',
};

/** Which icon + colour + label represents a trigger marker. */
function markerFor(t: TriggerMarker) {
  const source = t.source || 'ai_event';
  if (source === 'motion') {
    return { Icon: SOURCE_ICONS.motion, colour: TRIGGER_COLOURS.motion, label: 'Motion detected' };
  }
  if (source === 'manual' || source === 'api') {
    return { Icon: SOURCE_ICONS.manual, colour: TRIGGER_COLOURS.manual, label: 'Manually recorded' };
  }
  const type = t.event_type || 'other';
  return {
    Icon: EVENT_ICONS[type] || EVENT_ICONS.other,
    colour: TRIGGER_COLOURS.event,
    label: EVENT_LABELS[type] || EVENT_LABELS.other,
  };
}

/**
 * Identity of a marker for STACKING purposes: two triggers share a key when they'd draw the
 * same icon. Person + vehicle + motion on one block are three keys and therefore three stacked
 * symbols; four person detections are one key and one symbol carrying a count.
 */
function markerKey(t: TriggerMarker): string {
  const source = t.source || 'ai_event';
  if (source === 'motion') return 'motion';
  if (source === 'manual' || source === 'api') return 'manual';
  return `event:${t.event_type || 'other'}`;
}

/**
 * How many distinct symbols one cluster may stack before the rest collapse into a "+N".
 *
 * The lane grows to fit the tallest stack, so this is what stops a camera running every
 * detector at once (9 event categories + motion + manual) from pushing the bar 130px down the
 * screen. Four covers every real combination we've seen.
 */
const MAX_STACK = 4;
/** Icon box + the gap between stacked icons, in px. Drives the lane's computed height. */
const MARKER_PX = 11;
const MARKER_GAP_PX = 1;

interface ClipSelection {
  start: Date | null;
  end: Date | null;
}

interface RecordingTimelineProps {
  segments?: Segment[];
  selectedDate?: string | null;
  rangeStart?: string;
  rangeEnd?: string;
  currentTime?: Date | null;
  onSeek?: (d: Date) => void;
  playing?: boolean;
  /**
   * Clip range-select mode. When true, clicks on the bar define a download range
   * (first click = start, second = end, auto-ordered) instead of seeking, and the
   * selected band is drawn on the bar. State lives in the parent.
   */
  selectMode?: boolean;
  selection?: ClipSelection;
  onSelectionChange?: (sel: ClipSelection) => void;
  /** Retention windows to mark on the bar (event-based recording). */
  triggers?: TriggerMarker[];
  /**
   * The camera's recording mode. Only used to word the empty state and the legend: on a
   * triggered camera "no footage here" is expected behaviour, not a fault, and saying so
   * prevents the support ticket that a blank timeline otherwise generates.
   *
   * Accepts null because that is the natural "not loaded yet / unknown" state on both pages.
   */
  recordingMode?: string | null;
  /**
   * Show the zoom slider + pan track. Off gives exactly the old fixed-range behaviour, so a
   * caller that embeds the bar somewhere too cramped for the extra two rows can opt out.
   */
  zoomable?: boolean;
  /** Tightest span the zoom slider can reach, in minutes. */
  minSpanMinutes?: number;
  /**
   * Caller-owned controls (the Time range picker, segment count) rendered on the LEFT of the
   * timeline's own control row, with the zoom cluster pushed to the right of the same row.
   *
   * A slot rather than lifting zoom state into the pages: the two dashboards' RecordingsPage
   * have diverged, so anything held there has to be implemented and kept in sync twice.
   */
  controls?: ReactNode;
}

function fmtTime(d: Date) {
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}
function parseHM(str: string) {
  const [h, m] = (str || '00:00').split(':').map(Number);
  return h * 60 + (m || 0);
}

/**
 * Tick spacings, in SECONDS rather than minutes.
 *
 * Zoomed to a 5-minute view, minute ticks give five labels across the whole bar — too coarse
 * to read a time off, which is the entire point of zooming in. Below a minute the labels have
 * to carry seconds, so the ladder and the label format are chosen together.
 */
const TICK_STEPS_SEC = [5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 10800, 21600];
const TARGET_TICKS = 10;

function fmtTick(totalSec: number, withSeconds: boolean) {
  const s = ((Math.round(totalSec) % 86400) + 86400) % 86400;
  const hh = String(Math.floor(s / 3600)).padStart(2, '0');
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  if (!withSeconds) return `${hh}:${mm}`;
  return `${hh}:${mm}:${String(s % 60).padStart(2, '0')}`;
}

function fmtSpan(mins: number) {
  if (mins < 1) return `${Math.round(mins * 60)}s`;
  if (mins < 60) return `${Math.round(mins)}m`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

export default function RecordingTimeline({
  segments = [],
  selectedDate,
  rangeStart = '00:00',
  rangeEnd = '23:59',
  currentTime,
  onSeek,
  playing,
  selectMode = false,
  selection,
  onSelectionChange,
  triggers = [],
  recordingMode,
  zoomable = true,
  minSpanMinutes = 5,
  controls,
}: RecordingTimelineProps) {
  const barRef = useRef<HTMLDivElement>(null);
  const [hoverPct, setHoverPct] = useState<number | null>(null);
  const [hoverTime, setHoverTime] = useState<Date | null>(null);
  const [hoverOnSeg, setHoverOnSeg] = useState(false);
  const [dragging, setDragging] = useState(false);
  const lastSeekRef = useRef(0);

  const dayBase = useMemo(() => {
    if (!selectedDate) return new Date();
    const [y, m, d] = selectedDate.split('-').map(Number);
    return new Date(y, m - 1, d, 0, 0, 0, 0);
  }, [selectedDate]);

  // The APPLIED range — the outer bounds the view can pan within. This is what the parent
  // fetched a playlist for; zooming never leaves it, so zooming never invalidates playback.
  const rsMin = parseHM(rangeStart);
  const reMin = parseHM(rangeEnd);
  const fullMins = Math.max(reMin - rsMin, 1);

  // ── View window (zoom + pan) ────────────────────────────────────────────────────────
  // Purely a display concern. rangeStart/rangeEnd drive the backend playlist, so binding
  // zoom to them would rebuild the HLS source on every scroll tick and restart the video.
  // Instead the parent keeps loading the whole applied range and this magnifies part of it.
  const minSpan = Math.min(Math.max(minSpanMinutes, 0.25), fullMins);
  const canZoom = zoomable && fullMins > minSpan * 1.05;

  const [spanMins, setSpanMins] = useState(fullMins);
  const [viewStart, setViewStart] = useState(rsMin);
  // Suppresses playhead auto-follow briefly after a manual pan/zoom, so the view doesn't
  // yank itself back while the operator is still looking somewhere else.
  const manualUntilRef = useRef(0);

  // Any change to the applied range or the day is a new context — refit to the whole range
  // rather than leaving the user zoomed into a window that may no longer exist.
  useEffect(() => {
    setSpanMins(fullMins);
    setViewStart(rsMin);
  }, [rsMin, fullMins, selectedDate]);

  const span = clamp(spanMins, minSpan, fullMins);
  const vStart = clamp(viewStart, rsMin, Math.max(rsMin, reMin - span));
  const zoomed = span < fullMins - 0.01;

  /** Re-span the view while holding the time under `anchorPct` (0-100) in place. */
  const zoomToSpan = useCallback(
    (nextSpanRaw: number, anchorPct: number) => {
      const nextSpan = clamp(nextSpanRaw, minSpan, fullMins);
      const anchorMin = vStart + (clamp(anchorPct, 0, 100) / 100) * span;
      const nextStart = anchorMin - (clamp(anchorPct, 0, 100) / 100) * nextSpan;
      manualUntilRef.current = performance.now() + 4000;
      setSpanMins(nextSpan);
      setViewStart(clamp(nextStart, rsMin, Math.max(rsMin, reMin - nextSpan)));
    },
    [minSpan, fullMins, vStart, span, rsMin, reMin],
  );

  const panBy = useCallback(
    (deltaMins: number) => {
      manualUntilRef.current = performance.now() + 4000;
      setViewStart((s) => clamp(s + deltaMins, rsMin, Math.max(rsMin, reMin - span)));
    },
    [rsMin, reMin, span],
  );

  /**
   * Slider position ↔ span, LOGARITHMIC.
   *
   * Linear would spend most of the travel between 24h and 12h and cross the whole
   * 5m–60m region — the part that matters for finding an event — in the last few pixels.
   * t=0 is the full range ("minimum scroll"), t=1 is `minSpan` ("maximum scroll").
   */
  const ratio = Math.log(minSpan / fullMins);
  const spanToT = useCallback(
    (s: number) => (ratio === 0 ? 0 : clamp(Math.log(s / fullMins) / ratio, 0, 1)),
    [ratio, fullMins],
  );
  const tToSpan = useCallback((t: number) => fullMins * Math.exp(ratio * clamp(t, 0, 1)), [ratio, fullMins]);

  /**
   * Position as a percentage of the VISIBLE VIEW, UNCLAMPED — negative before the view,
   * >100 after it. Callers that need to know whether something falls outside the view must
   * use this; `pct` below clamps, which silently turns "off-screen" into "at the edge".
   */
  const rawPct = useCallback(
    (date: string | number | Date) => {
      const d = new Date(date);
      const minsSinceMidnight = (d.getTime() - dayBase.getTime()) / 60000;
      return ((minsSinceMidnight - vStart) / span) * 100;
    },
    [dayBase, vStart, span],
  );

  /** Clamped position, for drawing blocks that may straddle the edge of the view. */
  const pct = useCallback(
    (date: string | number | Date) => Math.max(0, Math.min(100, rawPct(date))),
    [rawPct],
  );

  const xToDate = useCallback(
    (x: number) => new Date(dayBase.getTime() + (vStart + x * span) * 60000),
    [dayBase, vStart, span],
  );

  const timeLabels = useMemo(() => {
    const spanSec = span * 60;
    const step =
      TICK_STEPS_SEC.find((s) => spanSec / s <= TARGET_TICKS) ||
      TICK_STEPS_SEC[TICK_STEPS_SEC.length - 1];
    const withSeconds = step < 60;

    const startSec = vStart * 60;
    const endSec = (vStart + span) * 60;
    const labels: { m: number; p: number; label: string }[] = [];
    const first = Math.ceil(startSec / step) * step;
    for (let s = first; s <= endSec; s += step) {
      const p = ((s - startSec) / (spanSec || 1)) * 100;
      if (p >= 0 && p <= 100) labels.push({ m: s, p, label: fmtTick(s, withSeconds) });
    }
    return labels;
  }, [vStart, span]);

  const isInSegment = useCallback(
    (date: Date) => {
      const ms = date.getTime();
      return segments.some((s) => ms >= new Date(s.start_time).getTime() && ms <= new Date(s.end_time).getTime());
    },
    [segments],
  );

  const posFromX = useCallback(
    (clientX: number) => {
      const rect = barRef.current!.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      return { x, d: xToDate(x) };
    },
    [xToDate],
  );

  // Seek anywhere on the bar (the parent snaps gaps to the nearest footage), throttled
  // during a drag so on-demand transcodes aren't hammered; always fire on press/release.
  const doSeek = useCallback(
    (d: Date, force: boolean) => {
      const now = performance.now();
      if (force || now - lastSeekRef.current > 200) {
        lastSeekRef.current = now;
        onSeek?.(d);
      }
    },
    [onSeek],
  );

  // Begin a scrub. Document listeners are attached SYNCHRONOUSLY here (not via a
  // state-driven effect) so a fast press/release can't slip through before they're
  // wired, and a missed mouseup can never leave the drag stuck.
  const startDrag = useCallback(
    (clientX: number) => {
      if (!barRef.current) return;
      setDragging(true);
      const first = posFromX(clientX);
      setHoverPct(first.x * 100);
      setHoverTime(first.d);
      setHoverOnSeg(isInSegment(first.d));
      doSeek(first.d, true);

      const move = (e: MouseEvent | TouchEvent) => {
        if (!barRef.current) return;
        const cx = 'touches' in e ? e.touches[0]?.clientX : (e as MouseEvent).clientX;
        if (cx == null) return;
        if ('touches' in e) e.preventDefault();
        const { x, d } = posFromX(cx);
        setHoverPct(x * 100);
        setHoverTime(d);
        setHoverOnSeg(isInSegment(d));
        doSeek(d, false);
      };
      const up = (e: MouseEvent | TouchEvent) => {
        const cx = 'changedTouches' in e ? e.changedTouches[0]?.clientX : (e as MouseEvent).clientX;
        if (barRef.current && cx != null) doSeek(posFromX(cx).d, true);
        setDragging(false);
        setHoverPct(null);
        setHoverTime(null);
        setHoverOnSeg(false);
        document.removeEventListener('mousemove', move);
        document.removeEventListener('mouseup', up);
        document.removeEventListener('touchmove', move);
        document.removeEventListener('touchend', up);
      };
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', up);
      document.addEventListener('touchmove', move, { passive: false });
      document.addEventListener('touchend', up);
    },
    [posFromX, isInSegment, doSeek],
  );

  // Clip range pick: first click sets start, second sets end (auto-ordered), a
  // third click after a complete range starts a fresh selection.
  const pickDate = useCallback(
    (d: Date) => {
      if (!onSelectionChange) return;
      const cur = selection || { start: null, end: null };
      if (!cur.start || (cur.start && cur.end)) {
        onSelectionChange({ start: d, end: null });
      } else {
        let a = cur.start;
        let b = d;
        if (b.getTime() < a.getTime()) { const t = a; a = b; b = t; }
        onSelectionChange({ start: a, end: b });
      }
    },
    [selection, onSelectionChange],
  );

  const pickPoint = useCallback(
    (clientX: number) => {
      if (!barRef.current || !onSelectionChange) return;
      pickDate(posFromX(clientX).d);
    },
    [posFromX, pickDate, onSelectionChange],
  );

  /**
   * Clicking a trigger symbol jumps to the moment it represents.
   *
   * The symbol is the thing an operator is actually looking for — "where did the person
   * appear" — so requiring them to then hit a 2px-tall bar underneath it, at the right
   * horizontal pixel, was making them aim at the wrong target. The exact event time is already
   * known here, so this is more precise than any click on the bar could be.
   *
   * Honours clip-select mode: while marking a download range, a marker click contributes its
   * timestamp to the range instead of seeking, so the operator can bracket an event exactly.
   */
  const activateMarker = useCallback(
    (d: Date) => {
      if (selectMode) pickDate(d);
      else doSeek(d, true);
    },
    [selectMode, pickDate, doSeek],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (dragging || !barRef.current) return; // drag handled by the document listener
      const { x, d } = posFromX(e.clientX);
      setHoverPct(x * 100);
      setHoverTime(d);
      setHoverOnSeg(isInSegment(d));
    },
    [dragging, posFromX, isInSegment],
  );
  const handleMouseLeave = useCallback(() => {
    if (dragging) return;
    setHoverPct(null);
    setHoverTime(null);
    setHoverOnSeg(false);
  }, [dragging]);

  /**
   * Wheel over the bar: zoom about the cursor; Shift (or a horizontal wheel) pans.
   *
   * Attached natively rather than via onWheel because React registers wheel listeners as
   * passive, where preventDefault is a no-op — without it every zoom also scrolls the page.
   */
  useEffect(() => {
    const el = barRef.current;
    if (!el || !canZoom) return;
    const onWheel = (e: WheelEvent) => {
      const horizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      e.preventDefault();
      if (e.shiftKey || horizontal) {
        const d = horizontal ? e.deltaX : e.deltaY;
        panBy((d / 120) * span * 0.15);
        return;
      }
      const rect = el.getBoundingClientRect();
      const anchor = rect.width ? ((e.clientX - rect.left) / rect.width) * 100 : 50;
      // Fixed multiplier per notch → constant perceived zoom speed at every scale.
      zoomToSpan(span * Math.pow(1.25, e.deltaY > 0 ? 1 : -1), anchor);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [canZoom, span, zoomToSpan, panBy]);

  const curPct = currentTime ? pct(currentTime) : null;

  /**
   * Keep the playhead on screen while playing.
   *
   * At a 5-minute view the playhead crosses the whole bar in five minutes and then plays on
   * invisibly, which makes zoom useless during playback. Recentring at 25% leaves most of the
   * view ahead of the playhead, where the footage the operator hasn't watched yet is.
   */
  useEffect(() => {
    if (!zoomed || !playing || !currentTime) return;
    if (performance.now() < manualUntilRef.current) return;
    const p = rawPct(currentTime);
    if (p >= 0 && p <= 88) return;
    const curMin = (new Date(currentTime).getTime() - dayBase.getTime()) / 60000;
    setViewStart(clamp(curMin - span * 0.25, rsMin, Math.max(rsMin, reMin - span)));
  }, [currentTime, playing, zoomed, rawPct, dayBase, span, rsMin, reMin]);

  /**
   * Drag the viewport rect along the overview track to pan.
   *
   * A visible, grabbable affordance rather than wheel-only: the bar itself is already claimed
   * by scrubbing, and on touch there is no wheel at all. Clicking anywhere on the track jumps
   * the view there, so it doubles as a coarse "go to this part of the day" control.
   */
  const panTrackRef = useRef<HTMLDivElement>(null);
  const [panning, setPanning] = useState(false);
  const startPan = useCallback(
    (clientX: number, grabOffsetPct: number | null) => {
      const track = panTrackRef.current;
      if (!track) return;
      setPanning(true);
      manualUntilRef.current = performance.now() + 4000;
      const widthPct = (span / fullMins) * 100;
      // Clicking the track (rather than the rect) centres the view on the click instead of
      // teleporting its left edge there, which would feel off by half a viewport.
      const grab = grabOffsetPct == null ? widthPct / 2 : grabOffsetPct;

      const apply = (cx: number) => {
        const rect = track.getBoundingClientRect();
        if (!rect.width) return;
        const p = ((cx - rect.left) / rect.width) * 100 - grab;
        setViewStart(clamp(rsMin + (p / 100) * fullMins, rsMin, Math.max(rsMin, reMin - span)));
        manualUntilRef.current = performance.now() + 4000;
      };
      apply(clientX);

      const move = (e: MouseEvent | TouchEvent) => {
        const cx = 'touches' in e ? e.touches[0]?.clientX : (e as MouseEvent).clientX;
        if (cx == null) return;
        if ('touches' in e) e.preventDefault();
        apply(cx);
      };
      const up = () => {
        setPanning(false);
        document.removeEventListener('mousemove', move);
        document.removeEventListener('mouseup', up);
        document.removeEventListener('touchmove', move);
        document.removeEventListener('touchend', up);
      };
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', up);
      document.addEventListener('touchmove', move, { passive: false });
      document.addEventListener('touchend', up);
    },
    [span, fullMins, rsMin, reMin],
  );

  /**
   * Trigger markers inside the visible range, clustered by position and then by KIND.
   *
   * A busy camera can produce a marker every few seconds; at 11px an icon spans roughly 1% of
   * the bar, so drawing them all yields an unreadable smear and hundreds of DOM nodes. Markers
   * closer together than one icon width therefore share a cluster.
   *
   * Within a cluster they are grouped by `markerKey` rather than folded into whichever arrived
   * first. That distinction is the point: a block that tripped a person detector AND a vehicle
   * detector AND motion used to render as a lone person icon reading "×3", which claims the
   * wrong thing happened three times. Now it renders all three symbols stacked, and a repeat of
   * the SAME kind is what carries a count.
   */
  const markerClusters = useMemo(() => {
    const MIN_GAP_PCT = 1.4;
    type Kind = { key: string; Icon: typeof UserIcon; colour: string; label: string; count: number };
    const out: { pct: number; time: Date; kinds: Kind[]; total: number }[] = [];
    for (const t of triggers) {
      // rawPct, not pct: a trigger outside a narrowed range must be dropped, not clamped
      // onto the edge of the bar where it would draw a marker at a time it never happened.
      const p = rawPct(t.event_time);
      if (p < 0 || p > 100) continue;
      const key = markerKey(t);
      const n = t.trigger_count || 1;
      const prev = out[out.length - 1];
      if (prev && p - prev.pct < MIN_GAP_PCT) {
        prev.total += n;
        const same = prev.kinds.find((k) => k.key === key);
        if (same) same.count += n;
        else prev.kinds.push({ key, ...markerFor(t), count: n });
        continue;
      }
      out.push({
        pct: p,
        // Earliest event in the cluster: clicking a marker should land at the START of what it
        // represents, not partway through it.
        time: new Date(t.event_time as string),
        kinds: [{ key, ...markerFor(t), count: n }],
        total: n,
      });
    }
    return out;
  }, [triggers, rawPct]);

  /**
   * Lane height tracks the tallest stack, so icons never overlap the bar or each other.
   *
   * Stacks are bottom-anchored, so anything the lane fails to account for grows UPWARD into
   * whatever sits above the timeline — hence the overflow badge is measured too, not just the
   * icons.
   */
  const markerLanePx = useMemo(() => {
    let tallest = 1;
    let anyOverflow = false;
    for (const c of markerClusters) {
      tallest = Math.max(tallest, Math.min(c.kinds.length, MAX_STACK));
      if (c.kinds.length > MAX_STACK) anyOverflow = true;
    }
    return tallest * (MARKER_PX + MARKER_GAP_PX) + (anyOverflow ? 8 : 0) + 4; // +4 pointer hairline
  }, [markerClusters]);

  /**
   * Which trigger classes actually appear in this view — drives the legend.
   *
   * Built from the SEGMENTS, not the triggers: the legend has to explain the colours of the
   * blocks on the bar, and a block can be present with its trigger marker outside the visible
   * range. Hidden entirely when everything is continuous, so the ordinary case stays quiet.
   */
  const legend = useMemo(() => {
    const seen = new Set<string>();
    for (const s of segments) {
      // Ends before the range starts, or starts after it ends → not on screen.
      if (rawPct(s.end_time) <= 0 || rawPct(s.start_time) >= 100) continue;
      seen.add(typeof s.trigger === 'string' ? s.trigger : 'continuous');
    }
    if (seen.size === 0) return [];
    if (seen.size === 1 && seen.has('continuous')) return [];
    return ['continuous', 'event', 'motion', 'manual'].filter((k) => seen.has(k));
  }, [segments, rawPct]);

  return (
    <div className="space-y-1">
      {currentTime && playing && (
        <div className="flex items-center gap-1.5 text-[11px] text-red-500 font-mono font-semibold">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          {fmtTime(currentTime)}
        </div>
      )}

      {/* ── Marker lane ──────────────────────────────────────────────────────────
          Sits ABOVE the bar, which is only 8-12px tall — an icon can't live inside it.
          Each marker is an icon with a hairline dropping into the bar, so it visibly points
          at the block it caused. This is what tells an operator at a glance that a green
          block exists because a person was detected, rather than because the camera records
          around the clock. */}
      {markerClusters.length > 0 && (
        <div className="relative" style={{ height: `${markerLanePx}px` }}>
          {markerClusters.map((c, i) => {
            const shown = c.kinds.slice(0, MAX_STACK);
            const hidden = c.kinds.length - shown.length;
            const when = fmtTime(c.time);
            // Edge-aware, exactly as the pan-track label: a centred tooltip on the first or
            // last marker of the day would hang half its width off the timeline.
            const tipPos: CSSProperties =
              c.pct < 12 ? { left: 0 }
              : c.pct > 88 ? { right: 0 }
              : { left: '50%', transform: 'translateX(-50%)' };
            return (
              <button
                key={`m-${i}-${c.pct}`}
                type="button"
                // `group` drives the hover card below — CSS-only, so hovering markers costs no
                // re-render of a component that also lays out every segment block.
                //
                // Bottom-anchored so every stack grows UPWARD from the bar: the pointer stays
                // on the bar no matter how many symbols sit above it.
                className="group absolute bottom-0 flex flex-col items-center cursor-pointer bg-transparent border-0 p-0 outline-none focus-visible:ring-1 focus-visible:ring-white/70 rounded-sm"
                style={{ left: `${c.pct}%`, transform: 'translateX(-50%)' }}
                // No `title`: the styled card below says the same thing without the ~1s delay,
                // and a native tooltip would surface underneath it a second later saying it
                // twice. aria-label carries the same content for assistive tech.
                aria-label={`Play from ${when}: ${c.kinds.map((k) => k.label).join(', ')}`}
                // The bar below starts a drag-scrub on mousedown; without this the press would
                // fall through to it and the seek would come from the pointer's X position
                // rather than the marker's exact event time.
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); activateMarker(c.time); }}
              >
                {/* Only the symbols brighten on hover, not the button: a CSS filter applies to
                    the whole subtree, so putting it on the button washed out the dark tooltip
                    below along with them. */}
                <span className="flex flex-col items-center group-hover:brightness-125">
                  {/* Reversed: kinds[0] — the first detection at this spot — ends up nearest the
                      bar, with later kinds stacked above it. */}
                  {[...shown].reverse().map((k) => (
                    <k.Icon
                      key={k.key}
                      className="shrink-0"
                      style={{ height: MARKER_PX, width: MARKER_PX, color: k.colour, marginBottom: MARKER_GAP_PX }}
                    />
                  ))}
                  {hidden > 0 && (
                    <span className="text-[8px] leading-none font-bold text-muted-foreground">+{hidden}</span>
                  )}
                  {/* The pointer. Without it a cluster of icons is ambiguous about which
                      block each one belongs to. */}
                  <span className="h-1 w-px" style={{ backgroundColor: c.kinds[0].colour }} />
                </span>

                {/* Hover card — NAMES the symbols. An icon is only recognisable once you
                    already know the vocabulary; until then a bolt and a truck are just shapes.
                    Every kind in the cluster gets a row, repeating its own icon in its own
                    colour so each line is tied to the symbol it explains, and overflow kinds
                    the "+N" hid are listed here too — this is the only place they're readable.

                    `bottom-full` puts it above the stack, clear of the bar and the block
                    colours. pointer-events-none so it can never sit between the pointer and
                    the marker and swallow the click that plays the clip. */}
                <span
                  className="pointer-events-none absolute bottom-full z-40 mb-1 hidden flex-col gap-0.5 rounded bg-foreground px-1.5 py-1 text-[9px] font-mono leading-none text-background shadow whitespace-nowrap group-hover:flex group-focus-visible:flex"
                  style={tipPos}
                >
                  {c.kinds.map((k) => (
                    <span key={k.key} className="flex items-center gap-1">
                      <k.Icon className="shrink-0" style={{ height: 9, width: 9, color: k.colour }} />
                      {k.label}
                      {k.count > 1 && <span className="opacity-60">×{k.count}</span>}
                    </span>
                  ))}
                  <span className="opacity-70">{when}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div
        ref={barRef}
        className="relative h-3 sm:h-2 rounded-full select-none overflow-visible touch-none"
        style={{ cursor: selectMode ? 'crosshair' : dragging ? 'grabbing' : 'pointer', backgroundColor: '#d1d5db' }}
        onMouseDown={(e) => { e.preventDefault(); if (selectMode) pickPoint(e.clientX); else startDrag(e.clientX); }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onTouchStart={(e) => { if (e.touches[0]) { if (selectMode) pickPoint(e.touches[0].clientX); else startDrag(e.touches[0].clientX); } }}
      >
        {segments.map((seg, i) => {
          const l = pct(seg.start_time);
          const r = pct(seg.end_time);
          if (r <= 0 || l >= 100) return null;
          const left = Math.max(l, 0);
          const width = Math.max(Math.min(r, 100) - left, 0.15);
          const trigger = typeof seg.trigger === 'string' ? seg.trigger : 'continuous';
          return (
            <div
              key={seg.id || i}
              className="absolute inset-y-0"
              title={trigger === 'continuous' ? undefined : `Kept by ${trigger} trigger`}
              style={{
                left: `${left}%`,
                width: `${width}%`,
                backgroundColor: TRIGGER_COLOURS[trigger] || TRIGGER_COLOURS.continuous,
              }}
            />
          );
        })}

        {curPct !== null && curPct > 0 && segments.map((seg, i) => {
          const l = pct(seg.start_time);
          const r = pct(seg.end_time);
          if (r <= 0 || l >= 100) return null;
          const segLeft = Math.max(l, 0);
          const segRight = Math.min(r, 100);
          if (segLeft >= curPct) return null;
          const fillRight = Math.min(segRight, curPct);
          return <div key={`p-${seg.id || i}`} className="absolute inset-y-0" style={{ left: `${segLeft}%`, width: `${fillRight - segLeft}%`, backgroundColor: '#3b82f6' }} />;
        })}

        {curPct !== null && curPct > 0 && (() => {
          const firstSegPct = segments.length > 0 ? Math.max(pct(segments[0].start_time), 0) : 100;
          const fillEnd = Math.min(firstSegPct, curPct);
          if (fillEnd > 0) {
            return <div className="absolute inset-y-0" style={{ left: '0%', width: `${fillEnd}%`, backgroundColor: '#93c5fd' }} />;
          }
          return null;
        })()}

        {/* A hairline in the bar at each trigger instant, matching the icon above it. The icon
            says WHAT happened; this says exactly WHEN, at a precision the icon can't. */}
        {markerClusters.map((c, i) => (
          <div
            key={`trg-${i}-${c.pct}`}
            className="absolute z-10 pointer-events-none opacity-70"
            style={{
              left: `${c.pct}%`, top: 0, bottom: 0, width: '1px',
              // Matches the icon nearest the bar when several are stacked.
              backgroundColor: c.kinds[0].colour,
            }}
          />
        ))}

        {curPct !== null && curPct >= 0 && curPct <= 100 && (
          <div className="absolute z-20 pointer-events-none" style={{ left: `${curPct}%`, top: '50%', transform: 'translate(-50%, -50%)' }}>
            <div className="w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-white shadow" />
          </div>
        )}

        {/* Empty state. On a triggered camera an empty range is EXPECTED — the mode only keeps
            footage around detections — so say that rather than leaving a blank bar that reads
            as a fault. */}
        {segments.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center px-2">
            <span className="text-[11px] text-muted-foreground text-center">
              {recordingMode && recordingMode !== 'continuous'
                ? 'No footage retained in this range — this camera only keeps footage around detections'
                : 'No footage in this range'}
            </span>
          </div>
        )}

        {selectMode && selection?.start && (() => {
          const sp = pct(selection.start);
          const ep = selection.end ? pct(selection.end) : sp;
          const left = Math.min(sp, ep);
          const width = Math.max(Math.abs(ep - sp), 0.4);
          const handle = (p: number, d: Date, key: string) => (
            <div key={key} className="absolute z-20 pointer-events-none" style={{ left: `${p}%`, top: '50%', transform: 'translate(-50%, -50%)' }}>
              <div className="w-1.5 h-4 rounded-sm border border-white shadow" style={{ backgroundColor: '#2563eb' }} />
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-mono px-1 py-0.5 rounded shadow whitespace-nowrap text-white" style={{ backgroundColor: '#2563eb' }}>
                {fmtTime(d)}
              </div>
            </div>
          );
          return (
            <>
              <div className="absolute inset-y-0 z-10 pointer-events-none" style={{ left: `${left}%`, width: `${width}%`, backgroundColor: 'rgba(37,99,235,0.35)' }} />
              {handle(sp, selection.start, 'sel-start')}
              {selection.end && handle(ep, selection.end, 'sel-end')}
            </>
          );
        })()}

        {hoverPct !== null && hoverTime && (
          <>
            <div className={`absolute w-px z-10 pointer-events-none ${hoverOnSeg ? 'bg-foreground/50' : 'bg-muted-foreground/30'}`} style={{ left: `${hoverPct}%`, top: -4, bottom: -4 }} />
            <div
              className={`absolute z-30 pointer-events-none -translate-x-1/2 text-[9px] font-mono px-1.5 py-0.5 rounded shadow whitespace-nowrap ${hoverOnSeg ? 'bg-foreground text-background' : 'bg-muted-foreground/70 text-background'}`}
              style={{ left: `${hoverPct}%`, top: -20 }}
            >
              {fmtTime(hoverTime)}
              {!hoverOnSeg && <span className="ml-1 opacity-60">—</span>}
            </div>
          </>
        )}
      </div>

      <div className="relative h-3 text-[8px] text-muted-foreground select-none">
        {timeLabels.map(({ m, p, label }) => (
          <div key={m} className="absolute" style={{ left: `${p}%` }}>
            <div className="w-px h-1.5 bg-border -mt-0.5" />
            <span className="absolute top-1.5 -translate-x-1/2 whitespace-nowrap">{label}</span>
          </div>
        ))}
      </div>

      {/* Slim overview track: the whole applied range, with the visible window as a
          draggable band. Sits directly under the timeline so the two bars read as one unit —
          the top bar is WHERE YOU ARE, this one is WHERE THAT IS IN THE DAY.

          The out-of-view parts are masked rather than the in-view part being tinted: tinting
          the window would sit on top of the segment blocks and fight their trigger colours,
          which are the whole point of the minimap. Dimming the rest leaves the selected span
          showing its true colours and still makes it the obvious focus.

          Gated on `zoomed`, NOT on `canZoom`: while the view still shows the whole range the
          band spans the full width and the masks have nothing to mask, so the track is a
          pixel-for-pixel restatement of the bar above it — a second draggable-looking line
          that does nothing when dragged. It only carries information once there is an
          off-screen remainder to point at. The zoom controls stay on `canZoom` so there is
          always a way to get there. */}
      {canZoom && zoomed && (() => {
        const leftPct = ((vStart - rsMin) / fullMins) * 100;
        const widthPct = Math.max((span / fullMins) * 100, 1.2);
        return (
          // Padded wrapper, not margin: the visible track is only 4px, far too thin to hit.
          // The padding both opens the gap under the timeline and gives the pointer a ~16px
          // target, so the bar can be hairline-slim without becoming fiddly to use.
          <div
            className="pt-3 pb-1 touch-none"
            style={{ cursor: panning ? 'grabbing' : 'pointer' }}
            onMouseDown={(e) => { e.preventDefault(); startPan(e.clientX, null); }}
            onTouchStart={(e) => { if (e.touches[0]) startPan(e.touches[0].clientX, null); }}
            title="Drag to move the visible window"
          >
            {/* LIVE window bounds, tracking the band as it is dragged. Above the track and the
                band's own colour, so it reads as a property OF the band rather than of the
                track underneath — the fixed range bounds below are the thing it moves within.

                Near an edge it switches from centred to edge-aligned instead of being clamped:
                a clamped centre keeps translating -50% and hangs half the label off the side,
                which is exactly where the numbers matter most (window pushed hard against the
                start or end of the day). */}
            {(() => {
              const centre = leftPct + widthPct / 2;
              const pos: CSSProperties =
                centre < 12 ? { left: 0 }
                : centre > 88 ? { right: 0 }
                : { left: `${centre}%`, transform: 'translateX(-50%)' };
              return (
                <div className="pointer-events-none relative mb-1 h-3 select-none">
                  <span
                    className="absolute top-0 whitespace-nowrap font-mono text-[9px] font-semibold leading-none"
                    style={{ ...pos, color: VIEW_WINDOW_COLOUR }}
                  >
                    {fmtTick(vStart * 60, span < 10)}–{fmtTick((vStart + span) * 60, span < 10)}
                  </span>
                </div>
              );
            })()}

            <div ref={panTrackRef} className="relative h-1 rounded-full bg-muted overflow-hidden">
              {segments.map((seg, i) => {
                const l = ((new Date(seg.start_time).getTime() - dayBase.getTime()) / 60000 - rsMin) / fullMins * 100;
                const r = ((new Date(seg.end_time).getTime() - dayBase.getTime()) / 60000 - rsMin) / fullMins * 100;
                if (r <= 0 || l >= 100) return null;
                const left = Math.max(l, 0);
                const trigger = typeof seg.trigger === 'string' ? seg.trigger : 'continuous';
                return (
                  <div
                    key={`ov-${seg.id || i}`}
                    className="absolute inset-y-0"
                    style={{
                      left: `${left}%`,
                      width: `${Math.max(Math.min(r, 100) - left, 0.15)}%`,
                      backgroundColor: TRIGGER_COLOURS[trigger] || TRIGGER_COLOURS.continuous,
                    }}
                  />
                );
              })}

              {currentTime && (() => {
                const p = ((new Date(currentTime).getTime() - dayBase.getTime()) / 60000 - rsMin) / fullMins * 100;
                if (p < 0 || p > 100) return null;
                return <div className="absolute inset-y-0 w-px bg-red-500 z-10" style={{ left: `${p}%` }} />;
              })()}

              {/* Masks over everything outside the visible window. */}
              <div className="absolute inset-y-0 left-0 z-10 bg-card/80" style={{ width: `${leftPct}%` }} />
              <div className="absolute inset-y-0 right-0 z-10 bg-card/80" style={{ width: `${Math.max(100 - leftPct - widthPct, 0)}%` }} />

              {/* The selected window. An explicit hex, not the brand token: brand is a
                  per-deployment CSS variable, so on some themes it lands close to the
                  segment colours it has to be told apart from. Indigo is the one hue not
                  already spoken for by a trigger class, the red playhead or the blue
                  progress fill. At 4px it is a filled band rather than an outline —
                  borders have no room to read at this height. */}
              <div
                className="absolute inset-y-0 z-20 rounded-full"
                style={{
                  left: `${leftPct}%`,
                  width: `${widthPct}%`,
                  backgroundColor: VIEW_WINDOW_COLOUR,
                  boxShadow: `0 0 0 1px ${VIEW_WINDOW_COLOUR}`,
                  cursor: panning ? 'grabbing' : 'grab',
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const rect = panTrackRef.current?.getBoundingClientRect();
                  const grab = rect?.width ? ((e.clientX - rect.left) / rect.width) * 100 - leftPct : null;
                  startPan(e.clientX, grab);
                }}
                onTouchStart={(e) => {
                  const t = e.touches[0];
                  if (!t) return;
                  e.stopPropagation();
                  const rect = panTrackRef.current?.getBoundingClientRect();
                  const grab = rect?.width ? ((t.clientX - rect.left) / rect.width) * 100 - leftPct : null;
                  startPan(t.clientX, grab);
                }}
              />
            </div>

            {/* FIXED bounds of the applied range — the track's two endpoints. These never move
                while panning, which is the point: they are the ruler the live window above is
                read against, so "where am I in the day" needs no tooltip and no arithmetic.
                Muted and unbolded so the moving pair stays the thing the eye tracks. */}
            <div className="pointer-events-none relative mt-1 h-3 select-none font-mono text-[9px] leading-none text-muted-foreground">
              <span className="absolute left-0 top-0">{fmtTick(rsMin * 60, false)}</span>
              <span className="absolute right-0 top-0">{fmtTick(reMin * 60, false)}</span>
            </div>
          </div>
        );
      })()}

      {/* ── Control row: caller's controls left, zoom cluster right ───────────────
          Replaces "narrow the Time range, wait for a new playlist, scrub, repeat" with a
          direct manipulation loop. The applied range is untouched, so nothing reloads: the
          slider only changes how much of it the bar spans. */}
      {(controls || canZoom) && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-1 text-[11px] text-muted-foreground">
          {controls}
          {canZoom && (
            <div className="ml-auto flex items-center gap-1.5">
              <MagnifyingGlassMinusIcon className="h-2.5 w-2.5 shrink-0 opacity-70" />
              <input
                type="range"
                min={0}
                max={1000}
                step={1}
                value={Math.round(spanToT(span) * 1000)}
                // Zoom about the playhead when it is genuinely on screen, else about the
                // centre. Must test rawPct, not curPct: curPct is clamped, so an off-screen
                // playhead reads as exactly 0 or 100 and would pin the zoom to a bar edge.
                onChange={(e) => {
                  const raw = currentTime ? rawPct(currentTime) : null;
                  const anchor = raw != null && raw >= 0 && raw <= 100 ? raw : 50;
                  zoomToSpan(tToSpan(Number(e.target.value) / 1000), anchor);
                }}
                className="w-16 sm:w-24 h-0.5 cursor-ew-resize accent-brand"
                title="Zoom — or scroll over the timeline. Shift-scroll to pan."
                aria-label="Timeline zoom"
              />
              <MagnifyingGlassPlusIcon className="h-2.5 w-2.5 shrink-0 opacity-70" />
              <span className="font-mono text-[10px] tabular-nums text-foreground w-8 text-right">
                {fmtSpan(span)}
              </span>
              <button
                type="button"
                onClick={() => { manualUntilRef.current = 0; setSpanMins(fullMins); setViewStart(rsMin); }}
                disabled={!zoomed}
                className="rounded p-0.5 hover:bg-muted disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                title="Fit the whole selected range"
                aria-label="Fit range"
              >
                <ArrowsPointingOutIcon className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Legend ───────────────────────────────────────────────────────────────
          Rendered only when the view contains something other than plain continuous
          footage, so an ordinary continuous camera's timeline is unchanged. Without it the
          block colours are just colours; with it, the operator can read the bar. */}
      {legend.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1.5 text-[10px] text-muted-foreground">
          {legend.map((k) => (
            <span key={k} className="inline-flex items-center gap-1">
              <span
                className="inline-block h-2 w-2.5 rounded-sm"
                style={{ backgroundColor: TRIGGER_COLOURS[k] }}
              />
              {TRIGGER_LABELS[k]}
            </span>
          ))}
          {recordingMode && recordingMode !== 'continuous' && (
            <span className="inline-flex items-center gap-1">
              <span className="opacity-60">·</span>
              Gaps are expected — this camera only keeps footage around
              {recordingMode === 'motion'
                ? ' motion'
                : recordingMode === 'event'
                  ? ' detections'
                  : ' detections and motion'}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
